/**
 * AiPromptInput — AI 에 보낼 사람의 지시를 받는 입력칸. 저장소에 한 벌뿐이다.
 *
 * 지시는 언제나 **선택**이다 — 비우고 보낼 수 있고, 비면 요청에서 그 키를 아예 싣지 않는다.
 * 그 규칙은 여기가 아니라 API 계층에 있다(ProblemHubV2/variationApi.js 의 requestGeneration) —
 * 이 컴포넌트는 값을 받아 그리기만 하고 다듬지도, 요청 몸통을 만들지도 않는다.
 * 두 곳에서 다듬으면 어느 쪽이 참인지 갈린다.
 * (서버도 같은 규칙이다 — null·빈 문자열·공백만은 키를 안 보낸 것과 같다. BE 회신 §5-2.)
 *
 * ── 지킬 것 셋 ──
 * ① **상한은 이 파일의 상수 하나다.** prop 으로 열지 마라 — 앞으로 붙을 자리마다 값이 갈린다.
 *    500 은 오너 확정(2026-08-28)이고 레거시 probagent 가 쓰던 값을 그대로 가져왔다.
 *    **서버도 같은 500 으로 막는다** — 확정됐다(BE 회신 2026-08-29 §5-2,
 *    docs/exam-builder-v2/BE-20260829-reply-fe-0828.md · alembic f2a3b4c5d6e7). 초과하면 400
 *    (「user_prompt 는 500자 이하여야 합니다(현재 N자)」)이고 비문자열도 400 이다. 생성(generate)도 같은 상한.
 *    다만 **재는 자가 서로 다르다** — 서버는 공백을 떼고(strip) 코드포인트로 세고, 화면은 다듬기 전
 *    UTF-16 코드유닛으로 센다. 화면 쪽이 언제나 같거나 더 엄격해서, 여기를 통과한 값은 서버에서도
 *    통과한다(타건으로 400 을 받을 길이 사실상 없다). 값을 올리려면 **서버를 먼저** 올려야 한다.
 * ② **조건부 렌더·key 를 걸지 마라.** `{mode === 'x' ? <A/> : <B/>}` 나 `key={mode}` 로 갈아 끼우면
 *    모드를 바꾸는 순간 입력한 글자가 날아간다. 한 자리에 한 번만 그리고 얼굴은 props 로만 바꾼다.
 *    코드로는 막을 수 없는 종류라 여기 적어 둔다.
 * ③ **값은 호출자가 든다**(통제 컴포넌트). 그래서 지난 지시를 채워 넣는 일도 호출부의 setState
 *    한 줄이면 되고 새 prop 이 필요 없다.
 */
import { useId } from 'react';
import PropTypes from 'prop-types';

// 글자 상한. 세는 자(value.length — UTF-16)와 자르는 자(maxLength)가 **같아야** 한다 —
// 코드포인트로 세면 이모지·일부 한자에서 「499/500 인데 더 못 침」이 생긴다.
// 서버 자와는 다르다(위 ① — strip 후 코드포인트). 어긋나는 방향은 「화면이 더 엄격」 한쪽뿐이라,
// 이모지를 섞으면 500 을 다 못 채우는 손해만 나고 화면을 통과한 값이 400 으로 튕기지는 않는다.
export const AI_PROMPT_MAX = 500;

// 옵션·규칙이 지시를 이긴다는 안내. BE 회신 2026-08-29 §4-2 5-1 이 우선순위를 확정하고 이 문구까지
// 제안했다 — 서버는 지시를 프롬프트 **맨 끝**에 붙이면서 「규칙·출력 형식·옵션과 충돌하면 그쪽이 우선」을
// 함께 적는다(§6-2 원문 블록). 그래서 options.difficulty:'up' 과 「쉽게」가 같이 오면 난이도는 올라가고
// 「쉽게」는 조용히 무시된다. 화면이 이 말을 안 하면 사람은 AI 가 말을 안 듣는다고 읽는다.
const OPTION_PRIORITY_TEXT = '지시는 선택한 옵션 안에서만 적용됩니다.';

export default function AiPromptInput({
  value, onChange, label, placeholder = '', rows = 2, disabled = false, className = '',
  showOptionPriority = false, ...rest
}) {
  const noticeId = useId();
  // maxLength 가 막으므로 타건으로는 넘길 수 없다. 넘는 경우는 호출부가 긴 값을 채워 넣었을 때뿐이라
  // 조용히 자르지 않고 실제 수를 붉게 보여 준다.
  const over = value.length > AI_PROMPT_MAX;
  return (
    <div className={className}>
      <textarea
        /* rest 를 맨 앞에 편다 — 뒤에 펴면 호출자가 maxLength·value·onChange 를 덮어쓸 수 있어
           「상한이 한 곳에서만 정해진다」가 코드로 강제되지 않는다. */
        {...rest}
        aria-label={label}
        aria-describedby={showOptionPriority ? noticeId : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={AI_PROMPT_MAX}
        className="w-full rounded-xl border p-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
      />
      <div className="mt-1 flex items-start gap-2">
        {/* 우선순위 안내는 **옵션이 있는 자리에서만** 켠다(기본 끔). 지시를 받는 열 자리 가운데 고를 옵션이
            있는 곳은 셋(단건 변형·시험지 AI 복사·문제 생성)뿐이고, 나머지(그림 재요청 둘·figspec 변환·
            OCR 실행·다시 읽기·분석 넷)에는 옵션이 아예 없다 — 거기서 「선택한 옵션」을 말하면 화면이
            있지도 않은 것을 가리키게 된다. 그래서 여기서 늘 그리지 않고 호출부가 켠다. */}
        {showOptionPriority && (
          <p id={noticeId} className="text-xs leading-relaxed text-gray-500">{OPTION_PRIORITY_TEXT}</p>
        )}
        {/* 상한이 있으면 화면이 그것을 말해야 한다 — 넘겨서 막히고 나서야 알게 두지 않는다.
            낭독기에는 내지 않는다: 타건마다 읽히면 입력을 방해하고, 이 칸을 쓰는 화면의
            assertive 토스트와 부딪친다. */}
        <p aria-hidden="true" className={`ml-auto shrink-0 text-xs ${over ? 'text-danger' : 'text-gray-500'}`}>
          {value.length}/{AI_PROMPT_MAX}
        </p>
      </div>
    </div>
  );
}

AiPromptInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired, // 이벤트가 아니라 문자열 하나를 받는다 — 형제 ConceptPicker 와 같은 모양
  label: PropTypes.string.isRequired,  // 눈에 보이는 라벨이 없다. 이것이 유일한 접근성 이름이다
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,         // 바깥 래퍼에 붙는다 — 위 요소와의 거리를 호출부가 정한다
  showOptionPriority: PropTypes.bool,  // 난이도·정답 유형 같은 옵션을 함께 보내는 자리에서만 true
};
